import { applyURLSearchParams } from "hsl-core/utils";
import { createStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { StoreActions, StoreState } from "./types";

const defaultState: StoreState = {
	appliedSort: {},
	isGrouped: false,
	openFilters: [],
	openFunds: [],
	openGroups: [],
	isOpenManagersFilter: false,
	view: "list",
	selectedManagers: [],
	cityWireImages: {},
	openFundTabs: {},
	filterRespSidePanelIsOpen: false,
	slidePanelScrollIsLockedMap: {},
	fundPanelResponsiveIsOpen: false,
};

const storeCreator = (initialState?: Partial<StoreState>) =>
	createStore<StoreState & StoreActions>()(
		devtools(
			immer((set, get) => ({
				...{ ...defaultState, ...initialState },
				doSort(key, order) {
					set({ appliedSort: { [key]: order } }, false, {
						type: `doSort: ${key} ${order}`,
						payload: { key, order },
					});
				},
				doToggleFilter(key) {
					set(
						(state) => {
							const openFilters = state.openFilters.includes(key)
								? state.openFilters.filter((f) => f !== key)
								: [...state.openFilters, key];
							return { openFilters };
						},
						false,
						{
							type: `doToggleFilter: ${key}`,
							payload: { key },
						},
					);
				},
				doToggleFund(localId) {
					set(
						(state) => {
							const openFunds = state.openFunds.includes(localId)
								? state.openFunds.filter((f) => f !== localId)
								: [...state.openFunds, localId];
							return { openFunds };
						},
						false,
						{
							type: `doToggleFund: ${localId}`,
							payload: { localId },
						},
					);
					// applyURLSearchParams({
					// 	"fund.id": get().openFunds.map((localId) => localId.split("-")[1]),
					// });
				},
				doToggleGroup(key) {
					set(
						(state) => {
							const openGroups = state.openGroups.includes(key)
								? state.openGroups.filter((f) => f !== key)
								: [...state.openGroups, key];
							return { openGroups };
						},
						false,
						{
							type: `doToggleGroup: ${key}`,
							payload: { key },
						},
					);
				},
				doToggleManagersFilter(flag) {
					set(
						(state) => ({
							isOpenManagersFilter:
								flag !== undefined ? flag : !state.isOpenManagersFilter,
						}),
						false,
						{
							type: `doToggleManagersFilter: ${flag}`,
							payload: { flag },
						},
					);
				},
				doToggleFundTab(key, tab) {
					set(
						(state) => {
							const openFundTabs = { ...state.openFundTabs, [key]: tab };
							return { openFundTabs };
						},
						false,
						{
							type: `doToggleFundTab: ${key} ${tab}`,
							payload: { key, tab },
						},
					);
				},
				doSetGrouped(isGrouped) {
					set({ isGrouped }, false, {
						type: `doSetGrouped: ${isGrouped}`,
						payload: { isGrouped },
					});
					applyURLSearchParams({ group: isGrouped ? "true" : "" });
				},
				doSetView(view) {
					set({ view }, false, {
						type: `doSetView: ${view}`,
						payload: { view },
					});
					applyURLSearchParams({ view: view === "grid" ? view : "" });
				},
				doSetSelectedManagers(selectedManagers) {
					set({ selectedManagers }, false, {
						type: `doSetSelectedManagers: ${selectedManagers}`,
						payload: { selectedManagers },
					});
				},
				doUpdateCityWireImages(cityWireImages) {
					set({ cityWireImages }, false, {
						type: `doUpdateCityWireImages: ${cityWireImages}`,
						payload: { cityWireImages },
					});
				},
				doToggleFiltersRespSidePanel(flag) {
					set({ filterRespSidePanelIsOpen: flag }, false, {
						type: `doToggleFiltersRespSidePanel: ${flag}`,
						payload: { filterRespSidePanelIsOpen: flag },
					});
				},
				doToggleFundPanelResponsive(flag) {
					set({ fundPanelResponsiveIsOpen: flag }, false, {
						type: `doToggleFundPanelResponsive: ${flag}`,
						payload: { fundPanelResponsiveIsOpen: flag },
					});
				},
				doSetSlidePanelScrollIsLockedMap(objectMap) {
					set(
						(state) => {
							const currentMap = state.slidePanelScrollIsLockedMap;
							return {
								slidePanelScrollIsLockedMap: {
									...currentMap,
									...objectMap,
								},
							};
						},
						false,
						{
							type: `doSetSlidePanelScrollIsLockedMap: ${objectMap}`,
							payload: { slidePanelScrollIsLockedMap: objectMap },
						},
					);
				},
			})),
			{
				name: "RLAM Fund Explorer",
				enabled: process.env.NODE_ENV !== "production",
				store: `RLAM FE -${(Math.random() * 100).toFixed(0)}`,
			},
		),
	);

export type FundExplorerStore = ReturnType<typeof storeCreator>;
export default storeCreator;
